import React, { useRef, useState, useCallback, useMemo, Fragment, useEffect } from 'react';
import moment from 'moment';
import { connect, useDispatch, useSelector } from 'react-redux';
import DayPicker, { LocaleUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { projectEntryActions, projectEntrySelectors, userActions, bulkEntryActions } from '../../store';
import { useToggle } from '@/hooks';
import { WeekSelection, HideCalendarButton } from './components';
import { MONTHS, SupportedSystems } from '../../shared';
import { getWeekDays, getWeekRange, isBeforeStartOfCurrentMonth } from '../../helpers';
import { StyledCalendarWrapper } from './styled';
import { Grid, Skeleton } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { mantineTheme as theme } from '../../theme';
import { UserDropdown } from './components/UserDropdown/UserDropdown';
import { NonAdminUser } from './components/UserDropdown/styled';
import { useMsal } from '@azure/msal-react';

const formatMonthTitle = (d) => `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
const localeUtilsOverride = { ...LocaleUtils, formatMonthTitle };

const Calendar = ({ selectedDays, config, activeMonth, currentSystem, isBulkEntryValid, isAddTaskValid, setShowBulkEntryModalOld, setShowBulkEntryModalNew, updateAllDates, showBulkEntry, completedWeeks }) => {
  const dispatch = useDispatch();
  const dayDblClickRef = useRef();
  const { instance } = useMsal();

  const userToken = instance.getActiveAccount();
  const user = useSelector(state => state.user.getUser.data);
  const numDirectReports = useSelector(state => state.user.getDirectReports.totalCount);

  const [modifiers, setModifiers] = useState();
  const [isVisible, toggleIsVisible] = useToggle(true);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  const { width } = useViewportSize();
  const calendarWrapperClassName = (`met__calendar ${!isVisible ? 'hide-met__calendar' : ''}`).trim();

  useEffect(() => {
    setCanEdit(moment(selectedDays[0]).isSameOrAfter(moment(), 'month'));
  }, [selectedDays]);

  useEffect(() => {
    if (userToken) {
      setIsSuperUser(userToken?.idTokenClaims?.roles?.includes('SuperUser'));
      dispatch(userActions.setActiveUser(userToken?.idTokenClaims));
      dispatch(userActions.getUser(userToken?.idTokenClaims?.oid));
      dispatch(userActions.getDirectReports(userToken?.idTokenClaims?.oid));
    }
  }, []);

  useMemo(() => {
    const completeWeek = completedWeeks.filter(w => w.completed).map(w => getWeekRange(moment(w.date)));
    setModifiers({
      completeWeek
    })
  }, [completedWeeks]);

  const handleDayLeave = () => {
    const newModifiers = modifiers;
    if (newModifiers.hoverRange) {
      delete newModifiers.hoverRange;
    }
    setModifiers({
      ...newModifiers
    })
  };

  const handleDayEnter = (date) => {
    setModifiers({
      ...modifiers,
      hoverRange: getWeekRange(moment(date))
    });
  };

  const handlePrevWeek = useCallback(() => {
    const selectedDaysTemp = getWeekDays(getWeekRange(moment(selectedDays[0]).add(-7, 'd')).from);
    let activeMonthTemp;

    if (moment(activeMonth).isBetween(selectedDaysTemp[0], selectedDaysTemp[6], 'days', '[]')) {
      activeMonthTemp = new Date(
        moment(activeMonth).add(-2, 'months').format('YYYY'),
        moment(activeMonth).add(-2, 'months').format('M')
      );
    } else if (moment(selectedDaysTemp[0]).add(-92, 'days') > moment(activeMonth) || moment(selectedDaysTemp[6]) < moment(activeMonth)) {
      activeMonthTemp = new Date(
        moment(activeMonth).add(-1, 'months').format('YYYY'),
        moment(selectedDaysTemp[0]).add(-1, 'months').format('M')
      );
    } else {
      activeMonthTemp = activeMonth;
    }

    updateAllDates(selectedDaysTemp, activeMonthTemp);
  }, [selectedDays, activeMonth, updateAllDates]);

  const handleNextWeek = useCallback(() => {
    const selectedDaysTemp = getWeekDays(getWeekRange(moment(selectedDays[0]).add(7, 'd')).from);
    let activeMonthTemp;

    if (moment(activeMonth).add(92, 'days').isBetween(selectedDaysTemp[0], selectedDaysTemp[6], 'days', '[]')) {
      activeMonthTemp = new Date(
        moment(activeMonth).add(0, 'months').format('YYYY'),
        moment(activeMonth).add(0, 'months').format('M')
      );
    } else if (moment(activeMonth) > moment(selectedDaysTemp[6]) || moment(activeMonth) < moment(selectedDaysTemp[0]).add(-92, 'days')) {
      activeMonthTemp = new Date(
        moment(selectedDaysTemp[6]).add(-1, 'months').format('YYYY'),
        moment(selectedDaysTemp[6]).add(-1, 'months').format('M')
      );
    } else {
      activeMonthTemp = activeMonth;
    }

    updateAllDates(selectedDaysTemp, activeMonthTemp);
  }, [selectedDays, activeMonth, updateAllDates]);

  const handleDayChange = useCallback((date) => {
    // Determine if the specific day was double clicked by user and the week it belongs to is not before current month (if so, open bulk entry modal)
    const curClickPerf = performance.now();
    const isRepeatedDateClick = dayDblClickRef.current && (dayDblClickRef.current.dateTime === date.getTime());

    if (isBulkEntryValid && isRepeatedDateClick && (curClickPerf - dayDblClickRef.current.startTime < 275) && !isBeforeStartOfCurrentMonth(moment(date))) {
      // Remove the setOld once we remove cosmos entry
      setShowBulkEntryModalOld(true);
      setShowBulkEntryModalNew(true);
    } else {
      dayDblClickRef.current = {
        dateTime: date.getTime(),
        startTime: curClickPerf,
      };

      // Short circuit process if it was a repeated click (but did not register as a dblClick)
      // ...OR if the selected day already falls in the currently selected week (no need to update selectedDays or activeMonth)
      const dateWeekRangeStart = getWeekRange(moment(date)).from;
      if (isRepeatedDateClick || dateWeekRangeStart.getTime() === selectedDays[0].getTime()) {
        return;
      }

      const newSelectedDays = getWeekDays(dateWeekRangeStart);
      let activeMonthTemp;

      if (moment(activeMonth).isBetween(newSelectedDays[0], newSelectedDays[6], 'days', '[]')) {
        activeMonthTemp = new Date(
          moment(activeMonth).add(-2, 'months').format('YYYY'),
          moment(activeMonth).add(-2, 'months').format('M')
        );
      } else if (moment(activeMonth).add(92, 'days').isBetween(newSelectedDays[0], newSelectedDays[6], 'days', '[]')) {
        activeMonthTemp = new Date(
          moment(activeMonth).add(0, 'months').format('YYYY'),
          moment(activeMonth).add(0, 'months').format('M')
        );
      } else {
        activeMonthTemp = activeMonth;
      }

      updateAllDates(newSelectedDays, activeMonthTemp);
    }
  }, [activeMonth, selectedDays, isBulkEntryValid, updateAllDates]);

  const numMonths = () => {
    if (width > parseInt(theme.breakpoints.lg)) {
      return 3;
    } else if (width > parseInt(theme.breakpoints.sm)) {
      return 2;
    } else {
      return 1;
    }
  }

  const showAddTaskButton = () => {
    if (!isAddTaskValid)
      return false;
    if ((currentSystem === SupportedSystems.VPIN) ||
      (currentSystem === SupportedSystems.LRS)) {
      return false;
    }

    return true;
  }

  return (
    <>
      <StyledCalendarWrapper columns={24}>
        <Grid.Col sm={8} xs={24}>
          {user == null ? <Skeleton height={30} /> : (
            <>
              {isSuperUser || numDirectReports > 0 ? (
                <UserDropdown
                  initialValue={user}
                  suggestAction={isSuperUser ? userActions.suggestUsers : userActions.suggestDirectReports}
                />
              ) : (
                <NonAdminUser>{user.displayName}</NonAdminUser>
              )}
            </>
          )}
          <hr />
          <WeekSelection
            selectedDays={selectedDays}
            handlePrevWeek={handlePrevWeek}
            handleNextWeek={handleNextWeek}
          />
        </Grid.Col>
        <Grid.Col md='auto' sm={12} xs={24} className={calendarWrapperClassName}>
          <DayPicker
            selectedDays={selectedDays}
            numberOfMonths={numMonths()}
            modifiers={modifiers}
            showCurrentAtPos={1}
            onDayClick={handleDayChange}
            onDayMouseEnter={handleDayEnter}
            onDayMouseLeave={handleDayLeave}
            month={activeMonth}
            localeUtils={localeUtilsOverride}
          />
        </Grid.Col>
        <Grid.Col sm='content' xs={0} className='hide-calendar-col-lg'>
          <HideCalendarButton
            isVisible={isVisible}
            toggleIsVisible={toggleIsVisible}
          />
        </Grid.Col>
      </StyledCalendarWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({
  showBulkEntry: state.projectEntry.bulkEntry.showModal,
  currentSystem: projectEntrySelectors.selectCurrentSystem(state),
  selectedDays: state.projectEntry.projectEntries.selectedDays,
  activeMonth: state.projectEntry.projectEntries.activeMonth,
  isBulkEntryValid: projectEntrySelectors.isCurrentSystemBulkEntryEnabled(state),
  isAddTaskValid: projectEntrySelectors.isAddTaskButtonEnabled(state),
  completedWeeks: state.projectEntry.completedWeeks,
  config: state.config.configuration
});

const mapDispatchToProps = (dispatch) => ({
  setShowBulkEntryModalOld: (show) => dispatch(projectEntryActions.setShowBulkEntryModal(show)),
  setShowBulkEntryModalNew: (show) => dispatch(bulkEntryActions.setShowBulkEntryModal(show)),
  updateAllDates: (selectedDays, activeMonth) => dispatch(projectEntryActions.updateAllDates(selectedDays, activeMonth))
});

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
